/**
 * VALUATION TYPES - Version 2.0
 * 
 * Interfaces e tipos para o sistema de valuation DCF
 * Refatorado conforme análise crítica (14/out/2025)
 * 
 * Correções implementadas:
 * ✅ WACC por empresa (Sétima e 555 separados)
 * ✅ Sequência FCFF canônica (NOPAT-first)
 * ✅ Sistema anti-dupla contagem KPD
 * ✅ ValuationSnapshot único
 */

// Decimal.js importado mas não utilizado - removido para evitar warning
// import Decimal from 'decimal.js';

// ============================================================================
// PREMISSAS MACROECONÔMICAS
// ============================================================================

/**
 * Modo de taxa (real vs nominal)
 * - real: Taxa ajustada pela inflação
 * - nominal: Taxa incluindo inflação
 */
export type RateMode = 'real' | 'nominal';

/**
 * Configuração de taxas reais/nominais (P0.2)
 * Garante consistência entre WACC e g na fórmula de Gordon
 */
export interface RateConfiguration {
  /** Modo do WACC (sempre nominal na prática) */
  waccMode: 'nominal';
  
  /** Modo do g (crescimento perpétuo) */
  gMode: RateMode;
  
  /** Inflação para conversão Fisher */
  inflation: number;
}

export interface MacroAssumptions {
  /** Taxa Selic (Risk-free rate) - Ex: 0.1175 (11,75%) */
  taxaSelic: number;
  
  /** Inflação IPCA - Ex: 0.045 (4,5%) */
  inflacao: number;
  
  /** Crescimento do PIB - Ex: 0.025 (2,5%) */
  crescimentoPIB: number;
  
  /** Prêmio de Risco Brasil (EMBI+) - Ex: 0.035 (3,5%) */
  premioRiscoBrasil: number;
  
  /** Prêmio de Risco de Mercado - Ex: 0.08 (8%) */
  premioRiscoMercado: number;

  // ============================================================================
  // AUDITORIA P0.1: Campos adicionais para transparência CAPM
  // ============================================================================

  /** Modo de taxa livre de risco (local = Selic, us_treasury = US Treasury 10Y) */
  rfMode?: RfMode;

  /** Metodologia de aplicação do CRP (multiplicative ou additive) */
  crpMethodology?: CRPMethodology;

  /** ERP Global (Damodaran) - Ex: 0.055 (5,5%) */
  erpGlobal?: number;

  /** Size Premium para small cap - Ex: 0.03 (3%) */
  sizePremium?: number;

  /** Specific Risk total (antes do breakdown) - Ex: 0.05 (5%) */
  specificRisk?: number;

  // ============================================================================
  // AUDITORIA P0.2: Configuração Real vs Nominal
  // ============================================================================

  /** Configuração de taxas real/nominal (P0.2) */
  rateConfig?: RateConfiguration;
}

// ============================================================================
// PREMISSAS POR EMPRESA
// ============================================================================

export interface CompanyAssumptions {
  /** Nome da empresa */
  nome: 'Sétima' | '555 Studios';
  
  /** ✅ NOVO: Último ano histórico da DRE (base para projeções) */
  baseYear: number;
  
  /** Receita base (último ano histórico) */
  receitaBase: number;
  
  /** EBITDA base (último ano histórico) */
  ebitdaBase: number;
  
  /** 
   * ✅ REFATORADO: Taxas de crescimento por ano (dinâmico - baseYear+1 até baseYear+5)
   * 
   * Exemplo: Se baseYear = 2024, conterá { 2025: 0.08, 2026: 0.09, ..., 2029: 0.07 }
   * Exemplo: Se baseYear = 2025, conterá { 2026: 0.08, 2027: 0.09, ..., 2030: 0.07 }
   */
  crescimento: Record<number, number> & { perpetuo: number };
  
  /** 
   * ✅ REFATORADO: Margens EBITDA target por ano (dinâmico - baseYear+1 até baseYear+5) 
   * 
   * Exemplo: Se baseYear = 2024, conterá { 2025: 0.13, 2026: 0.15, ..., 2029: 0.22 }
   */
  margemEBITDA: Record<number, number>;
  
  /** Depreciação como % da receita - Ex: 0.03 (3%) */
  depreciacao_pct_receita: number;
  
  /** 
   * ✅ REFATORADO: CAPEX como % da receita por ano (dinâmico - baseYear+1 até baseYear+5)
   * 
   * Exemplo: { 2025: 0.05, 2026: 0.05, ..., 2029: 0.05 } se baseYear = 2024
   */
  capex_pct_receita: Record<number, number>;
  
  /** 
   * ✅ REFATORADO: Variação de NWC como % da variação de receita (dinâmico - baseYear+1 até baseYear+5)
   * 
   * Exemplo: { 2025: 0.035, 2026: 0.035, ..., 2029: 0.035 } se baseYear = 2024
   */
  nwc_pct_delta_receita: Record<number, number>;
  
  /** Taxa de imposto sobre EBIT - Ex: 0.18 (18%) */
  taxaImpostoEBIT: number;
  
  /** Beta desalavancado (setorial) */
  beta: number;
  
  /** WACC (Custo Médio Ponderado de Capital) */
  wacc: number;
  
  /** Taxa de crescimento perpétuo - Ex: 0.03 (3%) */
  g: number;
  
  /** Flag: KPD já está incorporado no WACC? */
  kpd_in_wacc: boolean;

  // ============================================================================
  // AUDITORIA P0.2: Campos Real vs Nominal
  // ============================================================================

  /** Modo do g (real ou nominal) */
  gMode?: RateMode;
  
  /** Taxa de crescimento perpétuo em termos reais */
  g_real?: number;
  
  /** Taxa de crescimento perpétuo em termos nominais (após Fisher) */
  g_nominal?: number;
}

// ============================================================================
// EQUITY BRIDGE
// ============================================================================

export interface EquityBridgeInputs {
  /** Dívida Líquida (se > 0, subtrai do EV) */
  dividaLiquida: number;
  
  /** Caixa Excedente (se > 0, adiciona ao EV) */
  caixaExcedente: number;
}

// ============================================================================
// DESCONTOS DE NEGOCIAÇÃO
// ============================================================================

export interface DiscountInputs {
  /** Modo de aplicação do KPD */
  kpd_mode: 'in_wacc' | 'as_haircut';
  
  /** Taxa de desconto KPD - Ex: 0.15 (15%) */
  kpd_rate: number;
  
  /** Discount for Lack of Control - Ex: 0.10 (10%) */
  dloc: number;
  
  /** Discount for Lack of Marketability - Ex: 0.27 (27%) */
  dlom: number;
}

// ============================================================================
// PROJEÇÃO ANUAL
// ============================================================================

export interface YearProjection {
  /** Ano da projeção - Ex: 2025 */
  year: number;
  
  /** Receita projetada */
  receita: number;
  
  /** EBITDA (Receita × Margem) */
  ebitda: number;
  
  /** Depreciação (Receita × %) */
  depreciacao: number;
  
  /** EBIT (EBITDA - Depreciação) ✅ NOVO */
  ebit: number;
  
  /** NOPAT (EBIT × (1 - Tax)) ✅ NOVO */
  nopat: number;
  
  /** CAPEX (Receita × %) */
  capex: number;
  
  /** Variação de Capital de Giro */
  delta_nwc: number;
  
  /** FCFF (NOPAT + Dep - CAPEX - ΔNWC) */
  fcff: number;
  
  /** Fator de desconto (1 / (1 + WACC)^t) */
  discountFactor: number;
  
  /** Valor Presente do FCFF */
  pv_fcff: number;
}

// ============================================================================
// VALUATION POR EMPRESA
// ============================================================================

export interface CompanyValuation {
  /** Nome da empresa */
  empresa: 'Sétima' | '555 Studios';
  
  /** Projeções 2025-2029 */
  projecoes: YearProjection[];
  
  /** Soma dos VP(FCFF) 2025-2029 */
  vp_fcff_total: number;
  
  /** FCFF do primeiro ano de perpetuidade (2030) */
  fcff_2030: number;
  
  /** Valor Terminal (ano 2029) */
  valorTerminal: number;
  
  /** Valor Presente do Valor Terminal */
  vp_valorTerminal: number;
  
  /** Enterprise Value (VP FCFF + VP VT) */
  ev: number;
  
  /** WACC usado no cálculo */
  wacc: number;
  
  /** Taxa de crescimento perpétuo */
  g: number;
}

// ============================================================================
// CONSOLIDADO
// ============================================================================

export interface ConsolidatedValuation {
  /** Enterprise Value Total (Sétima + 555) */
  EV_Total: number;
  
  /** Equity Value (antes dos descontos) */
  Equity: number;
  
  /** Equity Value Ajustado (após descontos) */
  Equity_Ajustado: number;
  
  /** WACC consolidado (ponderado por EV) - apenas referência */
  WACC_Mix: number;
  
  /** Breakdown do Equity Bridge */
  equityBridge: {
    ev: number;
    menos_divida: number;
    mais_caixa: number;
    equity: number;
  };
  
  /** Breakdown dos Descontos */
  discounts: {
    equity_pre: number;
    kpd_aplicado: boolean;
    kpd_valor: number;
    dloc_valor: number;
    dlom_valor: number;
    equity_pos: number;
    desconto_total_pct: number;
  };
}

// ============================================================================
// PARTICIPAÇÃO
// ============================================================================

export interface StakeInfo {
  /** Percentual de participação - Ex: 0.50 (50%) */
  percentual: number;
  
  /** Valor da participação (Equity Ajustado × %) */
  valor: number;
}

// ============================================================================
// VALIDAÇÕES
// ============================================================================

export interface ValidationResult {
  /** Nome da regra de validação */
  rule: string;
  
  /** Passou na validação? */
  passed: boolean;
  
  /** Mensagem explicativa */
  message: string;
  
  /** Severidade */
  severity: 'error' | 'warning' | 'info';
  
  /** Bloqueia cálculo? */
  blocking?: boolean;
}

// ============================================================================
// VALUATION SNAPSHOT (ESTADO ÚNICO)
// ============================================================================

export interface ValuationSnapshot {
  /** ID único do snapshot */
  id: string;
  
  /** Timestamp de criação */
  timestamp: Date;
  
  /** Versão do engine */
  version: string;
  
  /** Premissas macroeconômicas */
  macro: MacroAssumptions;
  
  /** Premissas Sétima */
  setimaInputs: CompanyAssumptions;
  
  /** Premissas 555 */
  studios555Inputs: CompanyAssumptions;
  
  /** Equity Bridge */
  equityBridge: EquityBridgeInputs;
  
  /** Descontos */
  discounts: DiscountInputs;
  
  /** Participação */
  stake: {
    percentual: number;
  };
  
  /** Valuation Sétima */
  setimaValuation: CompanyValuation;
  
  /** Valuation 555 */
  studios555Valuation: CompanyValuation;
  
  /** Consolidado */
  consolidado: ConsolidatedValuation;
  
  /** Valor da Participação */
  valorParticipacao: number;
  
  /** Validações executadas */
  validations: ValidationResult[];

  // ============================================================================
  // AUDITORIA P0.1: Breakdowns CAPM para transparência
  // ============================================================================

  /** Breakdown CAPM - Sétima */
  setimaCAPM?: {
    components: CAPMComponents;
    keBreakdown: KeBreakdown;
    waccBreakdown: WACCBreakdown;
    validation: {
      warnings: string[];
      errors: string[];
    };
  };

  /** Breakdown CAPM - 555 Studios */
  studios555CAPM?: {
    components: CAPMComponents;
    keBreakdown: KeBreakdown;
    waccBreakdown: WACCBreakdown;
    validation: {
      warnings: string[];
      errors: string[];
    };
  };
}

// ============================================================================
// INPUTS PARA CÁLCULO
// ============================================================================

export interface ValuationInputs {
  /** Premissas macroeconômicas */
  macro: MacroAssumptions;
  
  /** Premissas Sétima */
  setima: CompanyAssumptions;
  
  /** Premissas 555 Studios */
  studios555: CompanyAssumptions;
  
  /** Equity Bridge */
  equityBridge: EquityBridgeInputs;
  
  /** Descontos de negociação */
  discounts: DiscountInputs;
  
  /** Participação */
  stake: {
    percentual: number;
  };
}

// ============================================================================
// METADATA DE AUDITABILIDADE
// ============================================================================

export interface AuditDependency {
  /** ID do valor dependente */
  id: string;
  
  /** Nome legível */
  name: string;
  
  /** Valor */
  value: number;
  
  /** Caminho para drill-down */
  path: string;
}

export interface AuditMetadata {
  /** Fórmula em texto simples */
  formula: string;
  
  /** Fórmula em LaTeX (opcional) */
  formulaLatex?: string;
  
  /** Descrição do cálculo */
  description: string;
  
  /** Dependências (valores que alimentam este cálculo) */
  dependencies: AuditDependency[];
  
  /** Timestamp do cálculo */
  calculatedAt: Date;
  
  /** Premissas utilizadas */
  assumptions: string[];
  
  /** Fonte dos dados */
  dataSource: string;
  
  /** Nível de confiança */
  confidenceLevel: 'high' | 'medium' | 'low';
  
  /** Categoria */
  category: 'historical' | 'projection' | 'valuation' | 'assumption';
  
  /** Validações aplicadas */
  validations: ValidationResult[];
}

export interface AuditableValue {
  /** Valor calculado */
  value: number;
  
  /** ID único */
  id: string;
  
  /** Metadata de auditoria */
  audit: AuditMetadata;
}

// ============================================================================
// ERROS CUSTOMIZADOS
// ============================================================================

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class KPDDuplicateError extends ValidationError {
  constructor() {
    super('KPD não pode ser aplicado duas vezes (WACC + haircut)');
    this.name = 'KPDDuplicateError';
  }
}

export class WACCvsGError extends ValidationError {
  constructor(wacc: number, g: number, empresa: string) {
    super(
      `${empresa}: WACC (${(wacc * 100).toFixed(2)}%) deve ser maior que g (${(g * 100).toFixed(2)}%)`
    );
    this.name = 'WACCvsGError';
  }
}

// ============================================================================
// TIPOS PARA CONTEXTO HISTÓRICO E AUDITABILIDADE (NORMALIZAÇÃO DRE)
// ============================================================================

/**
 * Outlier identificado no histórico
 * Usado para documentar anos atípicos (ex: crise 2023)
 */
export interface OutlierYear {
  ano: number;
  tipo: 'colapso' | 'pico' | 'recuperacao';
  margemReal: number;
  margemEsperada: number;
  desvio: number;
  causa?: string;
  impactoNoModelo: string;
}

/**
 * Contexto histórico de uma empresa
 * Derivado da DRE normalizada (2020-2024)
 */
export interface HistoricalContext {
  empresa: 'Sétima' | '555 Studios';
  periodoBase: number;
  outliers: OutlierYear[];
  cagr: {
    periodo: string;
    valor: number;
  };
  margemPico: {
    ano: number;
    margem: number;
  };
  margemAtual: {
    ano: number;
    margem: number;
  };
  receitas: {
    ano: number;
    valor: number;
  }[];
  ebitdas: {
    ano: number;
    valor: number;
    margem: number;
  }[];
}

/**
 * Cenário de projeção (pessimista, base, otimista)
 */
export interface ScenarioDefinition {
  nome: string;
  descricao: string;
  crescimento: CompanyAssumptions['crescimento'];
  margemBruta: CompanyAssumptions['margemEBITDA'];   // Mesma estrutura: { [ano: number]: number }
  margemEBITDA: CompanyAssumptions['margemEBITDA'];
  margemLiquida: CompanyAssumptions['margemEBITDA']; // Mesma estrutura: { [ano: number]: number }
  premissa: string;
  probabilidade?: number;
}

// ============================================================================
// AUDITORIA P0.1: CAPM - Transparência Ke/WACC
// ============================================================================

/**
 * Metodologia de aplicação do CRP (Country Risk Premium)
 * - multiplicative: CRP embutido no β×(ERP+CRP)
 * - additive: CRP adicionado separadamente: Ke = Rf + β×ERP + CRP
 */
export type CRPMethodology = 'multiplicative' | 'additive';

/**
 * Modo de taxa livre de risco
 * - local: Selic (Brasil)
 * - us_treasury: US Treasury 10Y + CRP
 */
export type RfMode = 'local' | 'us_treasury';

/**
 * Componentes detalhados do CAPM para cálculo do Ke
 * Fórmula completa: Ke = Rf + β×(ERP+CRP?) + Size Premium + Specific Risk
 */
export interface CAPMComponents {
  /** Taxa livre de risco (Selic ou US Treasury) */
  rf: number;
  /** Beta desalavancado ou alavancado */
  beta: number;
  /** Equity Risk Premium (ERP) - prêmio de risco de mercado global */
  erp: number;
  /** Country Risk Premium (CRP) - prêmio de risco Brasil */
  crp: number;
  /** Metodologia de aplicação do CRP */
  crpMethodology: CRPMethodology;
  /** Prêmio de tamanho (Size Premium) */
  sizePremium: number;
  /** Risco específico total (Size + Liquidity + Key Person + Execution) */
  specificRisk: number;
  /** Ke resultante (cost of equity) */
  ke: number;
}

/**
 * Breakdown detalhado do Ke mostrando cada termo da equação
 * Para auditoria e transparência
 */
export interface KeBreakdown {
  /** Rf: Taxa livre de risco */
  rf: {
    valor: number;
    fonte: 'Selic' | 'US Treasury 10Y';
    label: string;
  };
  /** β × (ERP + CRP?) ou β × ERP dependendo da metodologia */
  beta_x_market_risk: {
    beta: number;
    erp: number;
    crp?: number; // Só se crpMethodology = 'multiplicative'
    valor: number;
    label: string;
  };
  /** CRP aditivo (apenas se crpMethodology = 'additive') */
  crp_additive?: {
    valor: number;
    label: string;
  };
  /** Size Premium */
  sizePremium: {
    valor: number;
    label: string;
  };
  /** Specific Risk (agregado) */
  specificRisk: {
    valor: number;
    label: string;
    breakdown: SpecificRiskBreakdown;
  };
  /** Total Ke */
  total: {
    valor: number;
    label: string;
  };
}

/**
 * Breakdown do Risco Específico em componentes granulares
 */
export interface SpecificRiskBreakdown {
  /** Risco de tamanho (small cap) */
  size: number;
  /** Risco de liquidez (DLOM-related) */
  liquidity: number;
  /** Risco de pessoa-chave (Key Person Discount) */
  keyPerson: number;
  /** Risco de execução (projetos em andamento) */
  execution: number;
}

/**
 * Breakdown do WACC mostrando estrutura de capital
 */
export interface WACCBreakdown {
  /** Cost of Equity (Ke) */
  ke: {
    valor: number;
    peso: number; // E/(D+E)
    contribuicao: number; // Ke × peso
    breakdown: KeBreakdown;
  };
  /** Estrutura de capital (D/E ratio, % equity) */
  capitalStructure: {
    debtToEquity: number;
    equityWeight: number; // E/(D+E)
    debtWeight: number; // D/(D+E)
  };
  /** Cost of Debt (Kd) - opcional se houver dívida */
  kd?: {
    valor: number;
    valorAfterTax: number; // Kd × (1 - tax)
    peso: number; // D/(D+E)
    contribuicao: number; // Kd × (1-tax) × peso
  };
  /** WACC resultante */
  wacc: {
    valor: number;
    formula: string; // "Ke×E/(D+E) + Kd×(1-tax)×D/(D+E)"
  };
}
